import type { TerritoryResolution } from '@buzzmap/shared';
import { CATEGORY_SLUGS, categoryById, categoryBySlug } from '../routes/categorySlugs';
import { parsePresetRoute } from '../routes/territoryPaths';

interface TerritoryLinkLocation {
  pathname: string;
  search: string;
}

type TerritoryLinkResolution = Pick<TerritoryResolution, 'canonicalPath'> &
  Partial<Pick<TerritoryResolution, 'category' | 'period'>>;

/** Conserva la query pertinente e fa prevalere il periodo del nuovo link del server. */
export function territoryPeriodHref(path: string, search: string): string {
  if (!path.startsWith('/eventi/') || path.startsWith('//') || path.includes('\\')) return '/';
  const [pathname = '', targetSearch = ''] = path.split('?');
  const params = new URLSearchParams(search);
  params.delete('quando');
  const targetParams = new URLSearchParams(targetSearch);
  for (const [name, value] of targetParams) params.set(name, value);
  if (parsePresetRoute(pathname)?.periodInPath) params.delete('quando');
  const query = params.toString();
  return query ? `${pathname}?${query}` : pathname;
}

/**
 * The territory path a category page's URL is built on top of — mirrors
 * `territoryCanonicalPath` in `api/lib/render/html.ts` (T006-BE) so the
 * "Tutti gli eventi — {name}" link matches the server-rendered one exactly,
 * and so a category href built from a category-page resolution
 * (`/eventi/cremona/mercatini`) strips the slug instead of doubling it.
 * No automated cross-check between the two copies (fe-rev S2) — if the
 * server's stripping logic ever changes, update this one by hand too.
 *
 * (Lives here rather than in `TerritoryHeader` because the home category
 * tiles/chips build hrefs from it too — one copy, imported by both.)
 */
export function territoryCanonicalPath(resolution: TerritoryLinkResolution): string {
  const preset = parsePresetRoute(resolution.canonicalPath);
  if (preset) return preset.basePath;
  if (!resolution.category) return resolution.canonicalPath;
  const suffix = `/${resolution.category.slug}`;
  return resolution.canonicalPath.endsWith(suffix)
    ? resolution.canonicalPath.slice(0, -suffix.length)
    : resolution.canonicalPath;
}

/**
 * Href for a home category tile/chip — always an `<a href>` target, because
 * Google only follows anchors.
 *
 * - Territory resolved → `{territory canonical path}/{slug}` for every entry
 *   of `CATEGORY_SLUGS` (all 11 paged categories, not just
 *   `resolution.categoryLinks`: non-indexable pairs are fine, the server
 *   answers 200 + `noindex,follow` for any category slug under a territory).
 * - `altro` has no category page → `/esplora?category=altro`.
 * - No resolution (plain `/`) → `/esplora?category={id}`.
 */
export function categoryHref(
  categoryId: string,
  resolution: TerritoryLinkResolution | null,
  location?: TerritoryLinkLocation
): string {
  if (!resolution) return `/esplora?category=${categoryId}`;
  const entry = categoryById(categoryId);
  if (!entry) return `/esplora?category=${categoryId}`;
  return territoryCategorySelectionPath([categoryId], resolution, location);
}

/**
 * Reads the multi-category selection a territory URL carries in its
 * `?categorie=` param (chip-row territory mode, 2+ categories).
 *
 * - Unknown slugs are dropped ("invalid slugs are ignored" — the caller then
 *   normalizes 1 remaining slug to the real category page and 0 to the plain
 *   territory page).
 * - Duplicates collapse, and the result is always ordered by
 *   `CATEGORY_SLUGS`, so `?categorie=bambini,sagre` and
 *   `?categorie=sagre,bambini` are the same selection with the same
 *   canonical id list.
 * - Empty when the param is absent — an empty string counts as absent.
 *
 * Accepts a raw `location.search` (with or without leading `?`).
 */
export function parseCategorieParam(search: string): string[] {
  const raw = new URLSearchParams(search).get('categorie');
  if (!raw) return [];
  const ids = new Set<string>();
  for (const part of raw.split(',')) {
    const entry = categoryBySlug(part.trim());
    if (entry) ids.add(entry.id);
  }
  return CATEGORY_SLUGS.filter((c) => ids.has(c.id)).map((c) => c.id);
}

/**
 * URL for a category SELECTION on a territory route — what the chip row
 * navigates to after a plain left click, per the approved URL scheme:
 *
 * - 0 categories → the plain territory page (`territoryCanonicalPath`), which
 *   renders HomePage.
 * - exactly 1 → the real (indexed) category page `categoryHref(id, …)`.
 * - 2+ → `{territory}?categorie={slug,slug}` — slugs, deduped, in
 *   `CATEGORY_SLUGS` order regardless of click order, so the same selection
 *   always produces the same URL (the server answers 200 with canonical
 *   `/eventi/{territory}` for it, verified in production).
 *
 * Ids that aren't in `CATEGORY_SLUGS` (e.g. `altro`, no category page) are
 * dropped before the count — same validity rule as `parseCategorieParam`.
 */
export function territoryCategorySelectionPath(
  selectedIds: string[],
  resolution: TerritoryLinkResolution,
  location?: TerritoryLinkLocation
): string {
  const ids = new Set(selectedIds.filter((id) => categoryById(id)));
  const base = territoryCanonicalPath(resolution);
  const preset = parsePresetRoute(
    location?.pathname ?? resolution.canonicalPath,
    location?.search ?? ''
  );
  const period = preset?.periodSlug ?? resolution.period?.slug;
  const params = new URLSearchParams(location?.search ?? '');
  params.delete('categorie');
  let path = base;
  if (period) params.set('quando', period);
  if (ids.size === 1) {
    const entry = CATEGORY_SLUGS.find((category) => ids.has(category.id));
    if (entry) path = `${base}/${entry.slug}`;
  } else {
    if (preset?.periodInPath && period) {
      path = `${base}/${period}`;
      params.delete('quando');
    }
    if (ids.size >= 2) {
      params.set(
        'categorie',
        CATEGORY_SLUGS.filter((category) => ids.has(category.id))
          .map((category) => category.slug)
          .join(',')
      );
    }
  }
  // I separatori delle categorie restano leggibili come negli URL già pubblicati.
  const query = params.toString().replace(/%2C/g, ',');
  return query ? `${path}?${query}` : path;
}
